import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { sendUserInfoToContainer } from '../../../global-utils/containerUtils';
import storageAndCookie from '../../../global-utils/storage-and-cookies';
import {
  loginUser,
  logoutUser,
  userAuthLoadingEnd,
  userAuthLoadingStart
} from '../../../global-state/redux/actions';
import { apiRequest } from '../../../global-utils/api-request';
import { useIsAcesssTokenExpired } from '../../../global-utils';
import { getEventId } from '../../../global-utils/vmFunctions/core';
import { useSettingsWithManage } from '../../../modules/manage-config';
import { isDateInThePast } from '../../../global-utils/vmFunctions/dateTime';

/**
 * This component will only test the JWT token with the API of JME
 */
const TestJwtToken = (props) => {
  const { tokenFromJmtLogin, forceLogin } = props;
  const { appState } = useSelector((state) => state);
  const { instance, accessToken } = useSettingsWithManage('loginToJMTModule') ?? {};
  const instanceUser = `${instance}.user`;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const jwtTokenParams = params.get('jwt_token');
  const isAccessTokenExpired = useIsAcesssTokenExpired();

  useEffect(() => {
    /* token have priority:
      1. jwtTokenParams - from params: (?.jwt_token="yourtoken")
      2. tokenFromJmtLogin - token set by JmtLogin via AuthCatcher component that comes as a prop
      3. token from storage ${instance.user.accessToken}
    */

    const token = jwtTokenParams ?? tokenFromJmtLogin ?? accessToken;
    // we need the instance.user so that JMT Components can have the access to JMT accessToken if accessing the page via query ?jwtToken=
    if (token) {
      localStorage.setItem(
        instanceUser,
        JSON.stringify({ accessToken: token })
      );
    }
    const localSavedSession = storageAndCookie.get('access_token_all', true);
    if (localSavedSession && !jwtTokenParams && !forceLogin) {
      const isExpired = isDateInThePast(JSON.parse(localSavedSession)?.expires_at);
      if (isExpired) {
        dispatch(logoutUser());
      } else dispatch(loginUser(JSON.parse(localSavedSession)));
    } else if (forceLogin || token) {
      const url = `https://${appState.core.server_addr}/v0/logins/jwt.json`;
      dispatch(userAuthLoadingStart());
      apiRequest({
        url,
        body: {
          event_id: appState.core.event_id,
          jwt_token: token,
          include_user: 1,
          include_manage_admin_user_roles: 1,
          include_manage_api_user: 1
        },
        method: 'POST'
      })
        .then((res) => {
          if (res.data && !res.data.error_code && !res.data.error_message) {
            // successful login !
            dispatch(loginUser(res.data));
            dispatch(userAuthLoadingEnd());
            const { expires_at: expiresAt } = res.data;
            if (res.data?.manage_api_user?.token) {
              localStorage.setItem(
                instanceUser,
                JSON.stringify({ accessToken: res.data?.manage_api_user.token })
              );
            }
            storageAndCookie.set(
              'access_token',
              res.data?.access_token,
              new Date(expiresAt),
              true
            );
            storageAndCookie.set(
              'access_token_all',
              JSON.stringify(res.data),
              new Date(expiresAt),
              true
            );
            storageAndCookie.set(
              'webinar_access_token',
              res.data?.access_token,
              new Date(expiresAt),
              true
            );
            storageAndCookie.set(
              'firstname',
              res.data?.user?.firstname,
              new Date(expiresAt),
              true
            );
            storageAndCookie.set(
              'surname',
              res.data?.user?.surname,
              new Date(expiresAt),
              true
            );
            storageAndCookie.set(
              'user_id',
              res.data?.user?.id,
              new Date(expiresAt),
              true
            );
            sendUserInfoToContainer(res.data);
            if (window.location.pathname === '/auth/login') {
              navigate(storageAndCookie.get('loginRedirectUrl') || '/');
            }
          } else {
            dispatch(userAuthLoadingEnd());
            console.error(
              res.data?.error_message || 'Jwt token confirmation fail.'
            );
          }
        })
        .catch((e) => {
          console.log('GOT ERROR RES FROM CHEN!', e);
          dispatch(userAuthLoadingEnd());
        });
    } else {
      storageAndCookie.del('access_token', true);
      storageAndCookie.del('access_token_all', true);
      storageAndCookie.del('webinar_access_token', true);
      storageAndCookie.del('firstname', true);
      storageAndCookie.del('surname', true);
      storageAndCookie.del('user_id', true);
    }
  }, []);

  useEffect(() => {
    // For the quick fix, I use here eventId to
    // detect if we need to pass through the ota
    const usesOta = getEventId() === 585;
    if (isAccessTokenExpired) {
      let to = '/logout';
      if (usesOta) {
        to += `?externalUrl=${encodeURIComponent(
          'https://ers.m-anage.com/home/SignOut?returnURL=https://ers-id.m-anage.com/Account/LogoutExternal'
        )}`;
      }
      navigate(to);
    }
  }, [isAccessTokenExpired]);

  return null;
};

TestJwtToken.propTypes = {
  tokenFromJmtLogin: PropTypes.string,
  forceLogin: PropTypes.bool
};

export default TestJwtToken;
